'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCopyToClipboardResult {
  /** True while the "Copied" flash is active. Resets automatically after `flashMs`. */
  readonly copied: boolean;
  /** Attempts to copy `value` to the clipboard. Rejections are swallowed. */
  readonly copy: (value: string) => Promise<boolean>;
}

const DEFAULT_FLASH_MS = 1500;

/**
 * Shared click-to-copy state + action for UI shapes that can't use
 * `CopyButton` directly — full labeled buttons, copy-from-textarea
 * controls, and so on.
 *
 * Returns `{ copied, copy }`. Callers wire `copy(value)` to their
 * `onClick` and flip their own UI (icon toggle, label text) on `copied`.
 *
 * Invariants:
 *   - The flash timer is ref-tracked so rapid re-click always sees a
 *     full `flashMs` window (no leaked short timers stacking up).
 *   - Unmount cleanup cancels the pending timer so `setCopied(false)`
 *     never fires on an unmounted component.
 *   - `clipboard.writeText` rejections (permission denied, insecure
 *     context, document not focused, iframe without
 *     `allow="clipboard-write"`) are swallowed — the `copy` promise
 *     resolves to `false` instead of rejecting.
 *   - If `navigator.clipboard` is undefined (SSR, very old browsers),
 *     `copy` also resolves to `false`.
 */
export function useCopyToClipboard(flashMs?: number): UseCopyToClipboardResult {
  const effectiveFlashMs = flashMs ?? DEFAULT_FLASH_MS;
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current != null) {
        globalThis.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const copy = useCallback(
    async (value: string): Promise<boolean> => {
      if (typeof navigator === 'undefined' || !navigator.clipboard) return false;
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        if (resetTimerRef.current != null) {
          globalThis.clearTimeout(resetTimerRef.current);
        }
        resetTimerRef.current = globalThis.setTimeout(() => {
          setCopied(false);
          resetTimerRef.current = null;
        }, effectiveFlashMs);
        return true;
      } catch {
        return false;
      }
    },
    [effectiveFlashMs]
  );

  return { copied, copy };
}
